import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { cn } from '@/lib/utils/cn';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { useAdminLiveCampaigns, useCampaignReportDownload } from '../api';
import { BaseNumbersDialog } from '../components/BaseNumbersDialog';
import { buildCampaignColumns, DEFAULT_VISIBLE_COLUMNS } from '../columns';
import type { CampaignRow } from '../types';

export function AdminLiveCampaignsPage() {
  const query = useAdminLiveCampaigns();
  const rows = query.data ?? [];
  // SADMIN / sub-super-admin see the Parent Username column; resellers
  // (ROLE_ADMIN) don't — mirrors the `hidden: reseller` flag on the legacy
  // ListSuperadminVoiceCampaign column.
  const showParentUsername = useAuthStore((s) => s.isSubOrSuperAdmin());
  const [baseCampaignId, setBaseCampaignId] = useState<number | string | null>(null);

  type FilterChoice = 'none' | 'username' | 'campaignId';
  const FILTER_OPTIONS: Array<{ value: FilterChoice; label: string; numeric?: boolean }> = [
    { value: 'none', label: 'No filter' },
    { value: 'username', label: 'Username' },
    { value: 'campaignId', label: 'Campaign ID', numeric: true },
  ];

  const [filterChoice, setFilterChoice] = useState<FilterChoice>('none');
  const [filterDraft, setFilterDraft] = useState('');
  const [filterData, setFilterData] = useState('');

  const numericFilter = FILTER_OPTIONS.find((o) => o.value === filterChoice)?.numeric ?? false;

  const applyFilter = () => { setFilterData(filterDraft); };
  const clearFilter = () => { setFilterChoice('none'); setFilterDraft(''); setFilterData(''); };

  const filteredRows = useMemo(() => {
    if (filterChoice === 'none' || !filterData.trim()) return rows;
    const needle = filterData.trim().toLowerCase();
    return rows.filter((row) => {
      const val = String((row as unknown as Record<string, unknown>)[filterChoice] ?? '').toLowerCase();
      return numericFilter ? val === needle : val.includes(needle);
    });
  }, [rows, filterChoice, filterData, numericFilter]);

  const handleViewBase = (row: CampaignRow) => setBaseCampaignId(row.campaignId);

  const reportDownload = useCampaignReportDownload();
  const columns = useMemo(
    () =>
      buildCampaignColumns({
        showOwner: true,
        showParentUsername,
        onViewBase: showParentUsername ? handleViewBase : undefined,
        onDownloadReport: reportDownload.download,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showParentUsername, reportDownload.download],
  );

  // Admin views show owner columns by default.
  const visibility = {
    ...DEFAULT_VISIBLE_COLUMNS,
    userId: true,
    username: true,
    parentUsername: showParentUsername,
    grandparentUsername: showParentUsername,
    accountType: showParentUsername,
  };

  const canView = usePermission('Manage Campaign.Voice Campaign Summary', BASE_PERMISSION.VIEW);

  if (!canView) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="All Campaigns"
        description="Every live campaign across the users you manage. Refreshes every 30 seconds."
        actions={
          <Button
            variant="outline"
            size="icon"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw className={cn('h-4 w-4', query.isFetching && 'animate-spin')} />
          </Button>
        }
      />

      <DataTable<CampaignRow, unknown>
        tableId="campaigns:voice:admin-live:v1"
        columns={columns}
        data={filteredRows}
        isLoading={query.isLoading}
        searchPlaceholder="Search campaigns…"
        emptyTitle="No live campaigns"
        emptyDescription="When any of your users start a campaign, it appears here."
        getRowId={(row) => String(row.campaignId)}
        initialColumnPinning={{ right: ['actions'] }}
        initialColumnVisibility={visibility}
        exportFilename="voice-campaigns-admin-live.csv"
        pageSize={10}
        beforeTable={
          <div className="flex flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2">
            <span className="text-xs font-medium text-muted-foreground">Filter:</span>
            <Select
              value={filterChoice}
              onValueChange={(v) => {
                setFilterChoice(v as FilterChoice);
                setFilterDraft('');
                setFilterData('');
              }}
            >
              <SelectTrigger className="h-8 w-44">
                <SelectValue placeholder="No filter" />
              </SelectTrigger>
              <SelectContent>
                {FILTER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filterChoice !== 'none' && (
              <>
                <Input
                  value={filterDraft}
                  inputMode={numericFilter ? 'numeric' : 'text'}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFilterDraft(numericFilter ? v.replace(/[^\d]/g, '') : v);
                  }}
                  placeholder={numericFilter ? 'Enter ID' : 'Enter value'}
                  className="h-8 w-48"
                  onKeyDown={(e) => e.key === 'Enter' && filterDraft && applyFilter()}
                />
                <Button size="sm" onClick={applyFilter} disabled={!filterDraft}>
                  Apply
                </Button>
                <Button variant="ghost" size="sm" onClick={clearFilter}>
                  Clear
                </Button>
              </>
            )}
          </div>
        }
      />

      <BaseNumbersDialog campaignId={baseCampaignId} onClose={() => setBaseCampaignId(null)} />
    </FillPage>
  );
}
